// pages/ServiceManagementPage/ServiceManagementPage.tsx

import React, { useState, useMemo } from 'react';

import {

  Box,

  Typography,

  Tabs,

  Tab,

  TextField,

  InputAdornment,

  CircularProgress,

  Alert,

  Paper,

  IconButton,

  Chip,

  Grid,

  Collapse,

} from '@mui/material';

import {

  Search as SearchIcon,

  ExpandMore as ExpandMoreIcon,

  ChevronRight as ChevronRightIcon,

  Edit as EditIcon,

  Delete as DeleteIcon,

  Category as CategoryIcon,

  Settings as SettingsIcon,

  Label as LabelIcon,

} from '@mui/icons-material';

import { uniqBy } from 'lodash';



// Import Sidebar component (adjust the path as needed)

import { Sidebar } from '../../containers/Sidebar/Sidebar';

import { CustomTypography } from '../../components/CustomTypography/CustomTypography';

import { CustomButtonTable } from '../../components/CustomButtonTable/CustomButtonTable';



import {

  useGetAllCategories,

  useCreateCategory,

  useUpdateCategory,

  useDeleteCategory,

} from '../../api/category';

import {

  useGetAllServices,

  useCreateService,

  useUpdateService,

  useDeleteService,

} from '../../api/service';

import { Category, CategoryFormData, CreateCategoryRequest, Service, ServiceFormData, UpdateCategoryRequest } from '../../types/services.types';

import { serviceManagementPageStyles, servicesShellStyles } from './styles';

import CategoryForm from './CategoryForm/CategoryForm';

import ServiceForm from './ServiceForm/ServiceForm';

import CategoryManagementModal from './CategoryManagementModal/CategoryManagementModal';

import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

import { StationImport } from './StationImport/StationImport';

import Modal from './Modal/Modal';

import { useTranslation } from 'react-i18next';



// Page shell styles now live in ./styles.ts as `servicesShellStyles`.
// They used to be duplicated here, which shadowed the shell rules in that
// file and made edits there appear to do nothing.



interface TabPanelProps {

  children?: React.ReactNode;

  index: number;

  value: number;

}



const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {

  return (

    <div hidden={value !== index}>

      {value === index && <Box>{children}</Box>}

    </div>

  );

};



export const Services: React.FC = () => {

  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<number>(0);

  const [searchTerm, setSearchTerm] = useState<string>('');

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false);

  const [showServiceModal, setShowServiceModal] = useState<boolean>(false);

  const [showCategoryManagementModal, setShowCategoryManagementModal] = useState<boolean>(false);

  const [serviceForCategoryManagement, setServiceForCategoryManagement] = useState<Service | null>(null);



  // API hooks

  const { 

    categories = [], 

    isLoading: categoriesLoading, 

    error: categoriesError 

  } = useGetAllCategories(true);

  

  const { 

    services = [], 

    isLoading: servicesLoading, 

    error: servicesError 

  } = useGetAllServices();



  // 1. DEBUG: Raw API data - place right after API hooks

  console.log('=== RAW API DATA ===');

  console.log('Raw categories from API:', categories);

  console.log('Raw services from API:', services);

  

  const createCategoryMutation = useCreateCategory();

  const updateCategoryMutation = useUpdateCategory();

  const deleteCategoryMutation = useDeleteCategory();

  

  const createServiceMutation = useCreateService();

  const updateServiceMutation = useUpdateService(

    () => {

      setShowServiceModal(false);

      setSelectedService(null);

    }

  );

  const deleteServiceMutation = useDeleteService();



  // Process categories to remove duplicate services

  const processedCategories = useMemo(() => {

    return categories.map(category => ({

      ...category,

      services: category.services ? uniqBy(category.services, 'id') : []

    }));

  }, [categories]);



  // Process services to remove duplicates and deduplicate sign_categories

  const processedServices = useMemo(() => {

    return uniqBy(services, 'id').map(service => ({

      ...service,

      sign_categories: service.sign_categories ? uniqBy(service.sign_categories, 'id') : []

    }));

  }, [services]);



  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {

    setActiveTab(newValue);

    setSearchTerm('');

  };



  const toggleCategoryExpansion = (categoryId: string): void => {

    const newExpanded = new Set(expandedCategories);

    if (newExpanded.has(categoryId)) {

      newExpanded.delete(categoryId);

    } else {

      newExpanded.add(categoryId);

    }

    setExpandedCategories(newExpanded);

  };



  const handleCreateCategory = (data: CategoryFormData): void => {

    const parsedData: CreateCategoryRequest = {

      ...data,

      service_ids: data.service_ids.map(id => Number(id)),

    };



    createCategoryMutation.mutate(parsedData);

    setShowCategoryModal(false);

  };



  const handleUpdateCategory = (data: CategoryFormData): void => {

    if (selectedCategory) {

      const parsedData: UpdateCategoryRequest = {

        ...data,

        service_ids: data.service_ids.map(id => Number(id)),

      };



      updateCategoryMutation.mutate({

        id: Number(selectedCategory.id), 

        data: parsedData,

      });



      setShowCategoryModal(false);

      setSelectedCategory(null);

    }

  };



  const handleDeleteCategory = (id: string | number): void => {

    if (window.confirm('Are you sure you want to delete this category?')) {

      const categoryId = typeof id === 'string' ? Number(id) : id;

      deleteCategoryMutation.mutate(categoryId);

    }

  };



  const handleCreateService = (data: ServiceFormData): void => {

    createServiceMutation.mutate(data);

    setShowServiceModal(false);

  };



  const handleUpdateService = (data: ServiceFormData): void => {

    if (selectedService) {

      updateServiceMutation.mutate({ 

        serviceId: Number(selectedService.id), 

        data 

      });

    }

  };



  const handleDeleteService = (id: string | number): void => {

    if (window.confirm('Are you sure you want to delete this service?')) {

      deleteServiceMutation.mutate(typeof id === 'string' ? Number(id) : id);

    }

  };



  const filteredCategories = processedCategories.filter(category =>

    category.name.toLowerCase().includes(searchTerm.toLowerCase())

  );



  const filteredServices = processedServices.filter(service =>

    service.name.toLowerCase().includes(searchTerm.toLowerCase())

  );



  /*
   * Loading and error used to be separate `return` statements, each with its
   * own <Sidebar>. React treats those as different trees, so the whole shell
   * was destroyed and rebuilt every time the queries settled: the navigation
   * flickered on each load, an open mobile drawer closed itself, and the
   * sidebar's mount effects re-ran. The shell is rendered once now and only
   * the body inside it swaps.
   */
  const isLoadingData = categoriesLoading || servicesLoading;

  const loadError = categoriesError || servicesError;

  return (

    <Sidebar>

      <Box sx={servicesShellStyles.mainContainer}>

        {isLoadingData ? (

          <Box sx={serviceManagementPageStyles.loadingContainer}>

            <CircularProgress size={48} />

          </Box>

        ) : loadError ? (

          <Box sx={serviceManagementPageStyles.errorContainer}>

            <Alert severity="error" sx={serviceManagementPageStyles.errorAlert}>

              <Typography variant="h6" gutterBottom>

                {t('services.errorLoadingData', 'Error loading data')}

              </Typography>

              <Typography variant="body2">

                {loadError?.message || t('services.tryAgainLater', 'Please try again later.')}

              </Typography>

            </Alert>

          </Box>

        ) : (

          <>

        {/* Enhanced Header Section */}

        <Box sx={servicesShellStyles.headerSection}>

          <Box sx={servicesShellStyles.titleContainer}>

            <CustomTypography

              variant="h2"

              text={t("serviceManagement.title", "SERVICE MANAGEMENT")}

              isUppercase

            />

          </Box>



          {activeTab !== 2 && <CustomButtonTable

            variant="secondary"

            textVariant="link"

            label={t(`serviceManagement.add${activeTab === 0 ? 'Category' : 'Service'}`, `ADD ${activeTab === 0 ? 'CATEGORY' : 'SERVICE'}`)}

            width="fit-content"

            fontWeight="bold"

            textDecoration="none"

            iconName="plus"

            padding="8px 16px"

            textTransform="uppercase"

            onClick={() => {

              if (activeTab === 0) {

                setSelectedCategory(null);

                setShowCategoryModal(true);

              } else {

                setSelectedService(null);

                setShowServiceModal(true);

              }

            }}

          />}

        </Box>



        {/* Content Area */}

        <Box sx={servicesShellStyles.contentArea}>

          <Box sx={servicesShellStyles.contentPanel}>

            {/* Tabs */}

            <Box sx={servicesShellStyles.tabsContainer}>

              <Tabs 

                value={activeTab} 

                onChange={handleTabChange} 

                sx={{

                  '& .MuiTab-root': {

                    textTransform: 'none',

                    fontWeight: 500,

                    minHeight: 48,

                    fontSize: '14px',

                    color: '#666',

                    '&.Mui-selected': {

                      color: '#1976d2',

                      fontWeight: 600,

                    },

                  },

                  '& .MuiTabs-indicator': {

                    height: 3,

                    backgroundColor: '#1976d2',

                  },

                }}

              >

                <Tab

                  icon={<LabelIcon />}

                  iconPosition="start"

                  label={`Categories (${processedCategories.length})`}

                />

                <Tab

                  icon={<SettingsIcon />}

                  iconPosition="start"

                  label={`Services (${processedServices.length})`}

                />

                <Tab

                  icon={<LocalGasStationIcon />}

                  iconPosition="start"

                  label="Stations Gov"

                />

              </Tabs>

            </Box>



            {/* Search */}

            <Box sx={servicesShellStyles.searchContainer}>

              <TextField

                placeholder={`Search ${activeTab === 0 ? 'categories' : 'services'}...`}

                value={searchTerm}

                onChange={(e) => setSearchTerm(e.target.value)}

                variant="outlined"

                size="small"

                sx={servicesShellStyles.searchField}

                InputProps={{

                  startAdornment: (

                    <InputAdornment position="start">

                      <SearchIcon sx={{ color: '#999', fontSize: '20px' }} />

                    </InputAdornment>

                  ),

                }}

              />

            </Box>



            {/* Content */}

            <TabPanel value={activeTab} index={0}>

              <Box sx={serviceManagementPageStyles.contentContainer}>

                {filteredCategories.length === 0 ? (

                  <Box sx={serviceManagementPageStyles.emptyState}>

                    <LabelIcon sx={serviceManagementPageStyles.emptyIcon} />

                    <Typography variant="h6" sx={serviceManagementPageStyles.emptyTitle}>

                      No categories found

                    </Typography>

                    <Typography variant="body2" color="text.secondary">

                      Create your first category to get started.

                    </Typography>

                  </Box>

                ) : (

                  <Box sx={serviceManagementPageStyles.categoriesContainer}>

                    {filteredCategories.map(category => (

                      <Paper key={`category-${category.id}`} sx={serviceManagementPageStyles.categoryCard}>

                        <Box sx={serviceManagementPageStyles.categoryHeader}>

                          <Box sx={serviceManagementPageStyles.categoryInfo}>

                            <IconButton

                              onClick={() => toggleCategoryExpansion(String(category.id))}

                              size="small"

                              sx={serviceManagementPageStyles.expandButton}

                            >

                              {expandedCategories.has(String(category.id)) ? (

                                <ExpandMoreIcon />

                              ) : (

                                <ChevronRightIcon />

                              )}

                            </IconButton>

                            <Box>

                              <Typography variant="h6" sx={serviceManagementPageStyles.categoryName}>

                                {category.name}

                              </Typography>

                              <Typography variant="body2" color="text.secondary">

                                {category.services?.length || 0} services

                              </Typography>

                            </Box>

                          </Box>

                          

                          <Box sx={serviceManagementPageStyles.categoryActions}>

                            <IconButton

                              onClick={() => {

                                setSelectedCategory(category);

                                setShowCategoryModal(true);

                              }}

                              size="small"

                              sx={serviceManagementPageStyles.editButton}

                            >

                              <EditIcon />

                            </IconButton>

                            <IconButton

                              onClick={() => handleDeleteCategory(category.id)}

                              size="small"

                              sx={serviceManagementPageStyles.deleteButton}

                            >

                              <DeleteIcon />

                            </IconButton>

                          </Box>

                        </Box>

                        

                        <Collapse in={expandedCategories.has(String(category.id))}>

                          {category.services && category.services.length > 0 && (

                            <Box sx={serviceManagementPageStyles.categoryServices}>

                              <Grid container spacing={1.5}>

                                {category.services.map(service => (

                                  <Grid item xs={12} sm={6} md={4} key={`category-${category.id}-service-${service.id}`}>

                                    <Paper sx={serviceManagementPageStyles.serviceInCategory}>

                                      <Typography variant="body2" sx={serviceManagementPageStyles.serviceName}>

                                        {service.name}

                                      </Typography>

                                      {service.service_code && (

                                        <Typography variant="caption" color="text.secondary">

                                          {service.service_code}

                                        </Typography>

                                      )}

                                    </Paper>

                                  </Grid>

                                ))}

                              </Grid>

                            </Box>

                          )}

                        </Collapse>

                      </Paper>

                    ))}

                  </Box>

                )}

              </Box>

            </TabPanel>



            <TabPanel value={activeTab} index={1}>

              <Box sx={serviceManagementPageStyles.contentContainer}>

                {filteredServices.length === 0 ? (

                  <Box sx={serviceManagementPageStyles.emptyState}>

                    <SettingsIcon sx={serviceManagementPageStyles.emptyIcon} />

                    <Typography variant="h6" sx={serviceManagementPageStyles.emptyTitle}>

                      No services found

                    </Typography>

                    <Typography variant="body2" color="text.secondary">

                      Create your first service to get started.

                    </Typography>

                  </Box>

                ) : (

                  <Grid container spacing={3}>

                    {filteredServices.map(service => (

                      <Grid item xs={12} md={6} lg={4} key={`service-${service.id}`}>

                        <Paper sx={serviceManagementPageStyles.serviceCard}>

                          <Box sx={serviceManagementPageStyles.serviceHeader}>

                            <Box sx={serviceManagementPageStyles.serviceInfo}>

                              <Typography variant="h6" sx={serviceManagementPageStyles.serviceTitle}>

                                {service.name}

                              </Typography>

                              {service.service_code && (

                                <Chip

                                  label={service.service_code}

                                  size="small"

                                  sx={serviceManagementPageStyles.serviceCode}

                                />

                              )}

                              

                              {service.sign_categories && service.sign_categories.length > 0 && (

                                <Box sx={serviceManagementPageStyles.serviceCategories}>

                                  <Typography variant="caption" color="text.secondary" sx={serviceManagementPageStyles.categoriesLabel}>

                                    Categories:

                                  </Typography>

                                  <Box sx={serviceManagementPageStyles.categoryChips}>

                                    {service.sign_categories.map(category => (

                                      <Chip

                                        key={`service-${service.id}-category-${category.id}`}

                                        label={category.name}

                                        size="small"

                                        variant="outlined"

                                        sx={serviceManagementPageStyles.categoryChip}

                                      />

                                    ))}

                                  </Box>

                                </Box>

                              )}

                            </Box>

                            

                            <Box sx={serviceManagementPageStyles.serviceActions}>

                              <IconButton

                                onClick={() => {

                                  setServiceForCategoryManagement(service);

                                  setShowCategoryManagementModal(true);

                                }}

                                size="small"

                                sx={serviceManagementPageStyles.manageButton}

                                title="Manage Categories"

                              >

                                <CategoryIcon />

                              </IconButton>

                              <IconButton

                                onClick={() => {

                                  setSelectedService(service);

                                  setShowServiceModal(true);

                                }}

                                size="small"

                                sx={serviceManagementPageStyles.editButton}

                                title="Edit Service"

                              >

                                <EditIcon />

                              </IconButton>

                              <IconButton

                                onClick={() => handleDeleteService(service.id)}

                                size="small"

                                sx={serviceManagementPageStyles.deleteButton}

                                title="Delete Service"

                              >

                                <DeleteIcon />

                              </IconButton>

                            </Box>

                          </Box>

                        </Paper>

                      </Grid>

                    ))}

                  </Grid>

                )}

              </Box>

            </TabPanel>



            <TabPanel value={activeTab} index={2}>

              <StationImport />

            </TabPanel>



            {/* Modals */}

            <Modal

              isOpen={showCategoryModal}

              onClose={() => {

                setShowCategoryModal(false);

                setSelectedCategory(null);

              }}

              title={selectedCategory ? 'Edit Category' : 'Create Category'}

            >

              <CategoryForm

                category={selectedCategory || undefined}

                onSubmit={selectedCategory ? handleUpdateCategory : handleCreateCategory}

                onCancel={() => {

                  setShowCategoryModal(false);

                  setSelectedCategory(null);

                }}

              />

            </Modal>



            <Modal

              isOpen={showServiceModal}

              onClose={() => {

                setShowServiceModal(false);

                setSelectedService(null);

              }}

              title={selectedService ? 'Edit Service' : 'Create Service'}

            >

              <ServiceForm

                service={selectedService || undefined}

                onSubmit={selectedService ? handleUpdateService : handleCreateService}

                onCancel={() => {

                  setShowServiceModal(false);

                  setSelectedService(null);

                }}

              />

            </Modal>



            <CategoryManagementModal

              isOpen={showCategoryManagementModal}

              onClose={() => {

                setShowCategoryManagementModal(false);

                setServiceForCategoryManagement(null);

              }}

              service={serviceForCategoryManagement || undefined}

            />

          </Box>

        </Box>

          </>

        )}

      </Box>

    </Sidebar>

  );

};


